import CarrotCardClient from "../../../lib/carrot-card"
import Settings from "../../../utils/CarrotConfig"
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from "react-query"
import ResponseError from "../../../utils/ResponseError"
import { RetryConfig } from "utils/ErrorHandling"
import {
  CarrotCardAccount,
  Topup,
  TopupRequest,
  IsVerifiedResponse,
  VerifyMicrodepositsResponse,
  VerifyMicrodepositsRequest
} from "types/CarrotCardAccount"
const client = new CarrotCardClient(Settings.CARROT_BACKEND_URL)
const TOPUPS = "carrot-card/accounts/topups"
const ACCOUNT = "carrot-card/accounts"
const PRELAUNCH_OR_ACTIVE = "carrot-card/accounts/prelaunchOrActiveAccount"
const VERIFIED = "carrot-card/isSourceVerified"

export function useCarrotCardAccountTopups(accountId): UseQueryResult<Topup[], ResponseError> {
  return useQuery<Topup[], ResponseError>([TOPUPS, accountId], () => client.getTopups(accountId), {
    ...RetryConfig,
    enabled: !!accountId
  })
}

export function useCarrotCardAccount(accountId): UseQueryResult<CarrotCardAccount, ResponseError> {
  return useQuery<CarrotCardAccount, ResponseError>([ACCOUNT, accountId], () => client.getAccount(accountId), {
    ...RetryConfig,
    enabled: !!accountId
  })
}

export function usePreLaunchOrActiveCarrotCardAccount(companyId): UseQueryResult<CarrotCardAccount, ResponseError> {
  return useQuery<CarrotCardAccount, ResponseError>(
    [PRELAUNCH_OR_ACTIVE, companyId],
    () => client.getPrelaunchOrActiveCarrotCardAccount(companyId),
    {
      ...RetryConfig,
      enabled: !!companyId
    }
  )
}

export function useCreateTopup(accountId): UseMutationResult<Topup, ResponseError, TopupRequest> {
  const queryClient = useQueryClient()
  return useMutation<Topup, ResponseError, TopupRequest>(
    (topupRequest: TopupRequest) => client.createTopup(accountId, topupRequest),
    { ...RetryConfig, onSuccess: () => queryClient.invalidateQueries([TOPUPS, accountId]) }
  )
}
export function useCancelTopup(accountId): UseMutationResult<void, ResponseError, string> {
  const queryClient = useQueryClient()
  return useMutation<void, ResponseError, string>((topupId: string) => client.cancelTopup(accountId, topupId), {
    ...RetryConfig,
    onSuccess: () => {
      queryClient.invalidateQueries([TOPUPS, accountId])
      queryClient.invalidateQueries([ACCOUNT, accountId])
    }
  })
}

export function useIsAccountVerified(accountId): UseQueryResult<IsVerifiedResponse, ResponseError> {
  return useQuery<IsVerifiedResponse, ResponseError>([VERIFIED, accountId], () => client.isSourceVerified(accountId), {
    ...RetryConfig,
    enabled: !!accountId
  })
}

export function useVerifyMicrodeposits(accountId): UseMutationResult<VerifyMicrodepositsResponse, ResponseError> {
  const queryClient = useQueryClient()
  return useMutation<VerifyMicrodepositsResponse, ResponseError, VerifyMicrodepositsRequest>(
    (verifyMicrodepositsRequest) => client.verifyMicrodeposits(accountId, verifyMicrodepositsRequest),
    {
      ...RetryConfig,
      onSuccess: () => {
        queryClient.invalidateQueries([VERIFIED, accountId])
        queryClient.invalidateQueries([ACCOUNT, accountId])
      }
    }
  )
}
